import { jsPDF } from "jspdf";
import QRCode from "qrcode";
import { formatPrice, formatKm } from "@/lib/format";
import { carPowerKw } from "@/lib/taxBelgium";
import { parseSpecBlocks } from "@/lib/categorySpecs";

const NAVY = [15, 37, 64];
const NAVY2 = [30, 82, 136];
const SILVER = [155, 168, 183];
const INK = [15, 37, 64];
const GREY = [110, 122, 138];
const LINE = [223, 227, 233];
const SOFT = [240, 243, 247];

const BACKEND = process.env.REACT_APP_BACKEND_URL;
const proxied = (u) => (!u ? null : /^data:/i.test(u) ? u : `${BACKEND}/api/media/proxy?url=${encodeURIComponent(u)}`);

function loadImage(src) {
  return new Promise((res, rej) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => res(i); i.onerror = rej; i.src = src; });
}
// Cover-crop an image to a target aspect ratio, return jpeg dataURL (no overflow when placed in a box).
async function coverCrop(url, aspect, maxW = 1100) {
  const img = await loadImage(proxied(url));
  const targetR = aspect;
  let sw = img.width, sh = Math.round(sw / targetR);
  if (sh > img.height) { sh = img.height; sw = Math.round(sh * targetR); }
  const sx = Math.round((img.width - sw) / 2), sy = Math.round((img.height - sh) / 2);
  const cw = Math.min(maxW, sw), ch = Math.round(cw / targetR);
  const c = document.createElement("canvas"); c.width = cw; c.height = ch;
  c.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
  return c.toDataURL("image/jpeg", 0.85);
}

function loadLocalPng(path) {
  return new Promise((res) => {
    const i = new Image();
    i.onload = () => { const c = document.createElement("canvas"); c.width = i.naturalWidth; c.height = i.naturalHeight; c.getContext("2d").drawImage(i, 0, 0); res(c.toDataURL("image/png")); };
    i.onerror = () => res(null);
    i.src = path;
  });
}
const L = {
  fr: { sheet: "FICHE VÉHICULE", verified: "VENDEUR VÉRIFIÉ", used: "Occasion", warranty: "garantie",
    scan: "Scannez pour l'annonce complète",
    key: { reg: "1re immatriculation", km: "Kilométrage", fuel: "Carburant", gb: "Boîte", pw: "Puissance", color: "Couleur" },
    chars: "Caractéristiques", equip: "Équipements", desc: "Description",
    disc: "Estimation indicative des données. Pactly Car ne garantit pas l'exactitude.",
    tagline: "Marketplace auto vérifiée · Belgique & Europe" },
  nl: { sheet: "VOERTUIGFICHE", verified: "GEVERIFIEERDE VERKOPER", used: "Occasion", warranty: "garantie",
    scan: "Scan voor de volledige advertentie",
    key: { reg: "1e inschrijving", km: "Kilometerstand", fuel: "Brandstof", gb: "Versnellingsbak", pw: "Vermogen", color: "Kleur" },
    chars: "Kenmerken", equip: "Uitrusting", desc: "Beschrijving",
    disc: "Indicatieve schatting. Pactly Car garandeert de juistheid niet.",
    tagline: "Geverifieerde automarktplaats · België & Europa" },
  en: { sheet: "VEHICLE SHEET", verified: "VERIFIED SELLER", used: "Used", warranty: "warranty",
    scan: "Scan for the full listing",
    key: { reg: "First registration", km: "Mileage", fuel: "Fuel", gb: "Gearbox", pw: "Power", color: "Colour" },
    chars: "Specifications", equip: "Equipment", desc: "Description",
    disc: "Indicative data estimate. Pactly Car does not guarantee accuracy.",
    tagline: "Verified car marketplace · Belgium & Europe" },
};

export async function generateVehicleSheet(car, url, opts = {}) {
  const lang = opts.lang || "fr";
  const dealer = opts.dealer || {};
  const t = L[lang] || L.fr;
  const doc = new jsPDF({ unit: "mm", format: "a4" });
  const W = 210, H = 297, M = 14;
  const CW = W - 2 * M;
  const images = (car.images && car.images.length ? car.images : (car.image ? [car.image] : [])).filter(Boolean);
  const { clean, groups } = parseSpecBlocks(car.description);
  const charItems = groups.flatMap((g) => g.items).filter((it) => it.value).slice(0, 12);
  const equip = Array.isArray(car.options) ? car.options.filter(Boolean) : [];

  // ── Header ──────────────────────────────────────────────────────
  doc.setFillColor(...NAVY); doc.rect(0, 0, W, 24, "F");
  doc.setFillColor(...SILVER); doc.rect(0, 0, W, 1.6, "F");
  const logoData = await loadLocalPng(`${process.env.PUBLIC_URL || ""}/brand/pactly-lockup-white.png`);
  if (logoData) {
    const lw = 40, lh = lw / 4.492;
    doc.addImage(logoData, "PNG", M, 6, lw, lh, undefined, "FAST");
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...SILVER); doc.text(t.sheet, M + 0.5, 6 + lh + 3.6);
  } else {
    doc.setFillColor(...SILVER); doc.roundedRect(M, 5.5, 13, 13, 3, 3, "F");
    doc.setTextColor(...NAVY); doc.setFont("helvetica", "bold"); doc.setFontSize(12); doc.text("P", M + 4.3, 14.4);
    doc.setTextColor(255, 255, 255); doc.setFontSize(15); doc.text("Pactly", M + 17, 12);
    const pw = doc.getTextWidth("Pactly");
    doc.setTextColor(...SILVER); doc.text(" Car", M + 17 + pw, 12);
    doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.setTextColor(...SILVER); doc.text(t.sheet, M + 17, 17);
  }
  doc.setFillColor(...SILVER); doc.roundedRect(W - M - 42, 7, 42, 9, 4.5, 4.5, "F");
  doc.setTextColor(...NAVY); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.text(t.verified, W - M - 39, 13);

  // ── Dealer strip ────────────────────────────────────────────────
  let y = 30;
  const dName = dealer.name || car.seller_name || "";
  const contact = [dealer.location || car.seller_location, (dealer.phone || car.seller_phone) && `Tél. ${dealer.phone || car.seller_phone}`, dealer.email].filter(Boolean).join("   |   ");
  doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.text(dName || "—", M, y);
  if (contact) { doc.setFont("helvetica", "normal"); doc.setFontSize(8.5); doc.setTextColor(...GREY); doc.text(contact, M, y + 5); }
  y += 10;

  // ── Main photo (cover-crop, full width) ─────────────────────────
  const photoH = 62;
  doc.setFillColor(...SOFT); doc.roundedRect(M, y, CW, photoH, 3, 3, "F");
  try {
    if (images[0]) { const d = await coverCrop(images[0], CW / photoH); doc.addImage(d, "JPEG", M, y, CW, photoH, undefined, "FAST"); }
  } catch { /* keep soft box */ }
  y += photoH + 7;

  // ── Title + price box ───────────────────────────────────────────
  const priceBoxW = 58, priceBoxH = 20;
  doc.setTextColor(...INK); doc.setFont("helvetica", "bold"); doc.setFontSize(22);
  doc.text(`${car.make || ""} ${car.model || ""}`.trim(), M, y + 7, { maxWidth: CW - priceBoxW - 6 });
  if (car.variant) { doc.setFont("helvetica", "normal"); doc.setFontSize(10.5); doc.setTextColor(...GREY); doc.text(String(car.variant), M, y + 13, { maxWidth: CW - priceBoxW - 6 }); }
  const usedTxt = `${t.used}${car.warranty ? ` · ${car.warranty} ${t.warranty}` : ""}`;
  doc.setFillColor(...SOFT); doc.roundedRect(M, y + 17, doc.getTextWidth(usedTxt) + 7, 6, 3, 3, "F");
  doc.setTextColor(...NAVY2); doc.setFont("helvetica", "bold"); doc.setFontSize(7.5); doc.text(usedTxt, M + 3.5, y + 21);
  // price box
  doc.setFillColor(...NAVY); doc.roundedRect(W - M - priceBoxW, y - 2, priceBoxW, priceBoxH, 3, 3, "F");
  doc.setTextColor(...SILVER); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(t.sheet === "FICHE VÉHICULE" ? "PRIX" : "PRICE", W - M - priceBoxW + 5, y + 4);
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(18);
  doc.text(car.price ? `${formatPrice(car.price)},-` : "—", W - M - priceBoxW + 5, y + 14);
  y += 28;

  // ── Key facts ───────────────────────────────────────────────────
  doc.setDrawColor(...LINE); doc.setLineWidth(0.4); doc.line(M, y, W - M, y); y += 7;
  const kw = carPowerKw(car);
  const pwVal = car.power ? `${kw ? `${kw} kW ` : ""}(${car.power} CH)` : (kw ? `${kw} kW` : null);
  const kf = [
    [t.key.reg, car.first_registration || car.year],
    [t.key.km, car.mileage != null ? formatKm(car.mileage) : null],
    [t.key.fuel, car.fuel],
    [t.key.gb, car.transmission],
    [t.key.pw, pwVal],
    [t.key.color, car.color],
  ].filter(([, v]) => v != null && v !== "");
  const kfColW = CW / 3;
  doc.setFontSize(9.5);
  kf.forEach(([k, v], i) => {
    const col = i % 3, row = Math.floor(i / 3);
    const x = M + col * kfColW, ry = y + row * 12;
    doc.setFont("helvetica", "normal"); doc.setTextColor(...GREY); doc.setFontSize(8); doc.text(String(k).toUpperCase(), x, ry);
    doc.setFont("helvetica", "bold"); doc.setTextColor(...INK); doc.setFontSize(11.5); doc.text(String(v), x, ry + 5.4, { maxWidth: kfColW - 4 });
  });
  y += Math.ceil(kf.length / 3) * 12 + 4;
  doc.setDrawColor(...LINE); doc.line(M, y, W - M, y); y += 7;

  // ── Two columns: Caractéristiques / Équipements ─────────────────
  const gap = 8, colW = (CW - gap) / 2;
  const xL = M, xR = M + colW + gap;
  const startY = y;
  const bottomLimit = H - 60; // reserve for gallery + footer

  // LEFT: characteristics
  let ly = startY;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...NAVY); doc.text(t.chars, xL, ly); ly += 6;
  doc.setFontSize(9);
  if (charItems.length) {
    charItems.forEach((it) => {
      if (ly > bottomLimit) return;
      const valX = xL + (it.label ? 36 : 5);
      const valW = colW - (it.label ? 36 : 5);
      const vlines = doc.splitTextToSize(String(it.value), valW);
      doc.setFont("helvetica", "normal"); doc.setTextColor(...GREY);
      doc.text(it.label ? `${it.label}:` : "•", xL, ly);
      doc.setFont("helvetica", "bold"); doc.setTextColor(...INK);
      doc.text(vlines, valX, ly);
      ly += Math.max(1, vlines.length) * 5.2 + 0.8;
    });
  } else {
    doc.setFont("helvetica", "italic"); doc.setTextColor(...GREY); doc.text("—", xL, ly); ly += 5.6;
  }

  // RIGHT: equipment
  let ry = startY;
  doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...NAVY); doc.text(t.equip, xR, ry); ry += 6;
  doc.setFont("helvetica", "normal"); doc.setFontSize(9); doc.setTextColor(...INK);
  if (equip.length) {
    equip.forEach((o) => {
      if (ry > bottomLimit) return;
      const lines = doc.splitTextToSize(`•  ${o}`, colW);
      doc.text(lines, xR, ry); ry += lines.length * 4.8;
    });
  } else {
    doc.setFont("helvetica", "italic"); doc.setTextColor(...GREY); doc.text("—", xR, ry); ry += 5.6;
  }

  // ── Description (clean) full width if room ──────────────────────
  y = Math.max(ly, ry) + 4;
  if (clean && y < bottomLimit - 10) {
    doc.setDrawColor(...LINE); doc.line(M, y, W - M, y); y += 6;
    doc.setFont("helvetica", "bold"); doc.setFontSize(11); doc.setTextColor(...NAVY); doc.text(t.desc, M, y); y += 5.5;
    doc.setFont("helvetica", "normal"); doc.setFontSize(8.8); doc.setTextColor(60, 66, 78);
    const maxLines = Math.max(0, Math.floor((bottomLimit - y) / 4));
    const dl = doc.splitTextToSize(clean, CW).slice(0, maxLines);
    doc.text(dl, M, y, { lineHeightFactor: 1.4 });
  }

  // ── Gallery + QR (bottom) ───────────────────────────────────────
  const gy = H - 50;
  const gallery = images.slice(1, 4);
  try {
    const qr = await QRCode.toDataURL(url, { margin: 1, width: 200, color: { dark: "#0F2540", light: "#FFFFFF" } });
    doc.addImage(qr, "PNG", M, gy + 4, 24, 24);
    doc.setTextColor(...GREY); doc.setFont("helvetica", "normal"); doc.setFontSize(8); doc.text(t.scan, M, gy + 33, { maxWidth: 28 });
  } catch { /* optional */ }
  const gx0 = M + 30, tW = (W - M - gx0 - 2 * 4) / 3, tH = 30;
  for (let i = 0; i < gallery.length; i++) {
    try {
      const d = await coverCrop(gallery[i], tW / tH, 500);
      const gx = gx0 + i * (tW + 4);
      doc.setFillColor(...SOFT); doc.roundedRect(gx, gy, tW, tH, 2, 2, "F");
      doc.addImage(d, "JPEG", gx, gy, tW, tH, undefined, "FAST");
    } catch { /* skip */ }
  }

  // ── Footer ──────────────────────────────────────────────────────
  doc.setFillColor(...NAVY); doc.rect(0, H - 12, W, 12, "F");
  doc.setTextColor(...SILVER); doc.setFont("helvetica", "normal"); doc.setFontSize(7); doc.text(t.disc, M, H - 7.2);
  doc.setFontSize(8.5); doc.text(t.tagline, M, H - 3.5);
  doc.setTextColor(255, 255, 255); doc.setFont("helvetica", "bold"); doc.setFontSize(9); doc.text("pactlycar.be", W - M, H - 5, { align: "right" });

  doc.save(`pactly-fiche-${(car.make || "vehicule")}-${(car.model || "")}.pdf`.toLowerCase().replace(/\s+/g, "-"));
}
